import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, CheckCircle2, ToggleLeft, ToggleRight, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { getAllPlansForAdmin, createPlan, updatePlan, deletePlan } from '../services/apiClient';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PageHeader from '../components/ux/PageHeader';

const CURRENCY_SYMBOL = '₹';

const PLAN_COLORS = {
    starter: 'bg-card border-border',
    professional: 'bg-card border-primary/40',
    growth: 'bg-card border-primary/40',
    scale: 'bg-card border-border',
};

const DEFAULT_LIMITS = {
    maxActiveJobs: 2,
    maxInterviewsPerMonth: 40,
    maxUsers: 5,
    hasProctoring: false,
    hasAdvancedAnalytics: false,
    hasCustomJDTemplates: false,
    hasBrandingOnReports: false,
    hasWhiteLabelReports: false,
    hasDedicatedManager: false,
    hasSLASupport: false,
    hasAPIAccess: false,
    hasRoleBasedAccess: false,
};

const EMPTY_FORM = {
    name: '',
    slug: '',
    price: '',
    description: '',
    idealFor: '',
    isPopular: false,
    features: '',
    limits: { ...DEFAULT_LIMITS },
};

const BOOLEAN_LIMIT_KEYS = [
    'hasProctoring', 'hasAdvancedAnalytics', 'hasCustomJDTemplates',
    'hasBrandingOnReports', 'hasWhiteLabelReports', 'hasDedicatedManager',
    'hasSLASupport', 'hasAPIAccess', 'hasRoleBasedAccess',
];

const BOOLEAN_LIMIT_LABELS = {
    hasProctoring: 'Proctoring',
    hasAdvancedAnalytics: 'Advanced Analytics',
    hasCustomJDTemplates: 'Custom JD Templates',
    hasBrandingOnReports: 'Branding on Reports',
    hasWhiteLabelReports: 'White-label Reports',
    hasDedicatedManager: 'Dedicated Account Manager',
    hasSLASupport: 'SLA Support',
    hasAPIAccess: 'API Access',
    hasRoleBasedAccess: 'Role-based Access',
};

function PlanCard({ plan, onEdit, onDelete }) {
    const colorClass = PLAN_COLORS[plan.slug] || 'bg-card border-border';
    return (
        <div className={`relative rounded-2xl border-2 p-6 flex flex-col gap-4 ${colorClass}`}>
            {plan.isPopular && (
                <span className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full">
                    POPULAR
                </span>
            )}
            {!plan.isActive && (
                <span className="absolute top-4 left-4 bg-destructive text-destructive-foreground text-xs font-bold px-2 py-0.5 rounded-full">
                    INACTIVE
                </span>
            )}
            <div>
                <h3 className="text-xl font-bold text-foreground">{plan.name}</h3>
                <p className="text-3xl font-extrabold text-foreground mt-1">
                    {CURRENCY_SYMBOL}{plan.price.toLocaleString('en-IN')}
                    <span className="text-base font-normal text-muted-foreground"> /mo</span>
                </p>
                <p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
            </div>

            <div className="border-t border-border pt-4">
                <p className="text-xs text-primary font-semibold uppercase tracking-wider mb-2">Features Included</p>
                <ul className="space-y-1.5">
                    {plan.features?.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                            <CheckCircle2 size={14} className="text-primary shrink-0" />
                            {f}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <span>Max Jobs: <b className="text-foreground">{plan.limits?.maxActiveJobs === -1 ? '∞' : plan.limits?.maxActiveJobs}</b></span>
                <span>Interviews/mo: <b className="text-foreground">{plan.limits?.maxInterviewsPerMonth === -1 ? '∞' : plan.limits?.maxInterviewsPerMonth}</b></span>
                <span>Max Users: <b className="text-foreground">{plan.limits?.maxUsers === -1 ? '∞' : plan.limits?.maxUsers}</b></span>
            </div>

            <p className="text-xs text-muted-foreground italic mt-auto">Ideal for: {plan.idealFor}</p>

            <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => onEdit(plan)}>
                    <Pencil size={14} className="mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => onDelete(plan)}>
                    <Trash2 size={14} />
                </Button>
            </div>
        </div>
    );
}

function PlanFormModal({ open, onClose, editPlan, onSaved }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (editPlan) {
            setForm({
                ...editPlan,
                features: (editPlan.features || []).join('\n'),
                limits: { ...DEFAULT_LIMITS, ...editPlan.limits },
            });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [editPlan, open]);

    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
    const setLimit = (key, val) => setForm(f => ({ ...f, limits: { ...f.limits, [key]: val } }));

    const handleSubmit = async () => {
        if (!form.name || !form.slug || form.price === '') {
            toast.error('Name, slug, and price are required.');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                ...form,
                price: Number(form.price),
                features: form.features.split('\n').map(f => f.trim()).filter(Boolean),
                limits: {
                    ...form.limits,
                    maxActiveJobs: Number(form.limits.maxActiveJobs),
                    maxInterviewsPerMonth: Number(form.limits.maxInterviewsPerMonth),
                    maxUsers: Number(form.limits.maxUsers),
                },
            };
            if (editPlan) {
                await updatePlan(editPlan._id, payload);
                toast.success('Plan updated successfully.');
            } else {
                await createPlan(payload);
                toast.success('Plan created successfully.');
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{editPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-4 py-2">
                    <div className="space-y-1">
                        <Label>Plan Name</Label>
                        <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Starter" className="bg-muted/40 border-border" />
                    </div>
                    <div className="space-y-1">
                        <Label>Slug (unique identifier)</Label>
                        <Input value={form.slug} onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))} placeholder="starter" className="bg-muted/40 border-border" disabled={!!editPlan} />
                    </div>
                    <div className="space-y-1">
                        <Label>Price (₹/month)</Label>
                        <Input type="number" value={form.price} onChange={e => set('price', e.target.value)} placeholder="4000" className="bg-muted/40 border-border" />
                    </div>
                    <div className="space-y-1">
                        <Label>Ideal For</Label>
                        <Input value={form.idealFor} onChange={e => set('idealFor', e.target.value)} placeholder="1–30 employee companies." className="bg-muted/40 border-border" />
                    </div>
                    <div className="col-span-2 space-y-1">
                        <Label>Description</Label>
                        <Input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Best for small startups..." className="bg-muted/40 border-border" />
                    </div>
                    <div className="col-span-2 space-y-1">
                        <Label>Features (one per line)</Label>
                        <textarea
                            value={form.features}
                            onChange={e => set('features', e.target.value)}
                            rows={5}
                            className="w-full rounded-md bg-muted/40 border border-border text-foreground text-sm px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                            placeholder={"2 Active Jobs\n40 AI Interviews /mo\nResume Parsing"}
                        />
                    </div>

                    <div className="col-span-2">
                        <p className="text-sm font-semibold text-primary mb-3">Limits</p>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Max Active Jobs (-1=∞)</Label>
                                <Input type="number" value={form.limits.maxActiveJobs} onChange={e => setLimit('maxActiveJobs', e.target.value)} className="bg-muted/40 border-border" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Max Interviews/mo (-1=∞)</Label>
                                <Input type="number" value={form.limits.maxInterviewsPerMonth} onChange={e => setLimit('maxInterviewsPerMonth', e.target.value)} className="bg-muted/40 border-border" />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Max Users (-1=∞)</Label>
                                <Input type="number" value={form.limits.maxUsers} onChange={e => setLimit('maxUsers', e.target.value)} className="bg-muted/40 border-border" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            {BOOLEAN_LIMIT_KEYS.map(key => (
                                <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={!!form.limits[key]}
                                        onChange={e => setLimit(key, e.target.checked)}
                                        className="accent-primary"
                                    />
                                    <span className="text-sm text-muted-foreground">{BOOLEAN_LIMIT_LABELS[key]}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="col-span-2 flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={!!form.isPopular} onChange={e => set('isPopular', e.target.checked)} className="accent-primary" />
                            <span className="text-sm text-muted-foreground">Mark as Popular</span>
                        </label>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={loading}>
                        {loading ? 'Saving...' : editPlan ? 'Save Changes' : 'Create Plan'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function SuperAdminPlansPage() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editPlan, setEditPlan] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await getAllPlansForAdmin();
            setPlans(res.data || []);
        } catch {
            toast.error('Failed to load plans.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPlans(); }, []);

    const openCreate = () => { setEditPlan(null); setModalOpen(true); };
    const openEdit = (plan) => { setEditPlan(plan); setModalOpen(true); };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deletePlan(deleteTarget._id);
            toast.success(`"${deleteTarget.name}" plan deactivated.`);
            setDeleteTarget(null);
            fetchPlans();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className=" mx-auto">
            <PageHeader
                title="Pricing Plans"
                description="Manage subscription plans. Changes apply to new company assignments immediately."
                action={
                    <Button onClick={openCreate} className="gap-2">
                        <Plus size={16} /> Create Plan
                    </Button>
                }
            />

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-8">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="rounded-2xl bg-muted animate-pulse h-96" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-8">
                    {plans.map(plan => (
                        <PlanCard
                            key={plan._id}
                            plan={plan}
                            onEdit={openEdit}
                            onDelete={setDeleteTarget}
                        />
                    ))}
                </div>
            )}

            <PlanFormModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                editPlan={editPlan}
                onSaved={fetchPlans}
            />

            {/* Delete confirm */}
            <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Deactivate Plan</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground text-sm">
                        Are you sure you want to deactivate <b>{deleteTarget?.name}</b>? Existing companies on this plan will not be affected, but it won&apos;t be available for new assignments.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                            {deleting ? 'Deactivating...' : 'Deactivate'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
